import React, {useCallback, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Stack, styled} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import CancelSubscriptionModal from '@contentlab/components/cancel-subscription-modal/CancelSubscriptionModal';
import LtxScalerLoader from '@contentlab/components/ltx-scaler-loader';
import {ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import usePurchaseProcessStartedEvent from '@contentlab/hooks/analytics/use-purchase-process-started-event';
import useStoreFlowStartedEvent from '@contentlab/hooks/analytics/use-store-flow-started-event';
import useStoreViewDismissedEvent from '@contentlab/hooks/analytics/use-store-view-dismissed-event';
import useStoreViewPresentedEvent from '@contentlab/hooks/analytics/use-store-view-presented-event';
import useContactSales from '@contentlab/hooks/use-contact-sales';
import useOnboardingFlags from '@contentlab/hooks/use-onboarding-flags/useOnboardingFlags';
import useTrialUpgradeModal from '@contentlab/hooks/use-trial-upgrade-modal/useTrialUpgradeModal';
import {contentLabActions, ErrorKind} from '@contentlab/stores/contentLabStore';
import {FreeTrialEndModalReason} from '@contentlab/stores/freeTrialEndModalStore';
import onboardingWizardStore from '@contentlab/views/contentlab/pages/onboarding-page/onboardingWizardStore';
import PricingBundle from '@contentlab/views/contentlab/pricing/components/pricing-bundle';
import {
  ContactSalesButton,
  PricingCtaButton,
} from '@contentlab/views/contentlab/pricing/components/pricing-bundle/Buttons';
import MultiPlansPricingBundle from '@contentlab/views/contentlab/pricing/components/pricing-bundle/MultiPlansPricingBundle';
import TrialBanner from '@contentlab/views/contentlab/pricing/components/trial-banner/TrialBanner';
import {
  isFreePlan,
  isTrialPlan,
} from '@contentlab/views/contentlab/pricing/utils';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import Sentry from '@/services/sentry/Sentry';
import {
  CONTENT_LAB_VIEW_NAMES,
  CONTENT_STUDIO_PRICING_SOURCES,
  CONTENT_STUDIO_PRICING_VIEW_TYPES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import {
  getContentLabWebPaymentFlowId,
  removeContentLabWebPaymentFlowId,
} from '@/utils/LtidStorage';
import getDaysUntil from '@/utils/getDaysUntil';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import {ContentLabSubscription} from '@/types/models/pricing/contentLabSubscription';
import PlansCollection from '@/types/models/pricing/plansCollection';
import SubscriptionPlan from '@/types/models/pricing/subscriptionPlan';
import ContentLabLogo from '@/components/ContentLabLogo';
import Link from '@/components/link';
import useContentStudioCancelSubscriptionMutation from '@/hooks/content-studio/mutations/useContentStudioCancelSubscriptionMutation';
import useContentLabSubscriptionData from '@/hooks/use-content-lab-subscription-data';
import useHandlePricingCtaClick from '@/hooks/use-handle-pricing-cta-click';
import useNavigation from '@/hooks/use-navigation';
import usePrevious from '@/hooks/use-previous';
import styles from './ContentLabPricingPage.module.scss';

export type ContentLabPricingPageProps = {
  flags: {
    ltxScalerFreeTrial: boolean;
  };
};

function getBundlePriceFootnote(bundle: ContentLabPlanBundle) {
  if (bundle === ContentLabPlanBundle.STARTER) {
    return translate('Billed monthly');
  }
  if (bundle === ContentLabPlanBundle.ENTERPRISE) {
    return translate('Custom billing and package');
  }
  return '';
}

function getBundleDescription(bundle: ContentLabPlanBundle) {
  if (bundle === ContentLabPlanBundle.STARTER) {
    return translate('Basic plan to get started with AI-powered tools');
  }
  if (bundle === ContentLabPlanBundle.PRO) {
    return translate('Flexible plan to scale with AI-powered tools');
  }
  return translate('Custom plan with advanced AI tools and support');
}

function getBundleLabel(bundle: ContentLabPlanBundle) {
  if (bundle === ContentLabPlanBundle.STARTER) {
    return translate('Starter');
  }
  if (bundle === ContentLabPlanBundle.PRO) {
    return translate('Pro');
  }
  return translate('Enterprise');
}

function BottomSubscriptionInfo({
  activeSubscription,
  cancelSubscriptionHandler,
}: {
  activeSubscription?: ContentLabSubscription;
  cancelSubscriptionHandler: () => void;
}) {
  if (!activeSubscription || isFreePlan(activeSubscription)) return null;
  const isCancelledSubscription = !activeSubscription.isAutoRenewEnabled;
  if (isCancelledSubscription) {
    return (
      <div className={styles.bottomBar}>
        <Body size="md">
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: translate('full-access-until', {
                date: new Date(activeSubscription.renewAtMs).toLocaleDateString(
                  'en-US',
                  {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  }
                ),
              }),
            }}
          />
        </Body>
      </div>
    );
  }
  // active subscription and auto renew enabled
  return (
    <div className={styles.bottomBar}>
      <Body size="md">{translate('Want to end your subscription?')}</Body>
      <Body
        size="md"
        className={styles.clickableText}
        onClick={cancelSubscriptionHandler}
      >
        {translate('Cancel now')}
      </Body>
    </div>
  );
}

const skippedFlowStartedEventSources = [
  CONTENT_STUDIO_PRICING_SOURCES.PRICING_MODAL,
];

function ContentLabPricingPage(props: ContentLabPricingPageProps) {
  const {
    flags: {ltxScalerFreeTrial},
  } = props;
  const navigation = useNavigation();
  const location = useLocation();
  const previousLocation = usePrevious(location);
  const openTrialUpgradeModal = useTrialUpgradeModal();

  const {storeFlowStarted} = useStoreFlowStartedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {storeViewPresented} = useStoreViewPresentedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {storeViewDismissed} = useStoreViewDismissedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {purchaseProcessStarted} = usePurchaseProcessStartedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {uiInteraction} = useUiInteractionEvent();
  let presentation_id = '';
  const {
    activePlan,
    activeSubscription,
    plansCollections,
    isPlansDataFetched,
    isFetchingCurrentSubscription,
  } = useContentLabSubscriptionData();
  const {
    mutateAsync: cancelSubscription,
    isLoading: isCancelSubscriptionInProgress,
  } = useContentStudioCancelSubscriptionMutation();

  const isTrialPeriod = activeSubscription?.isTrialPeriod;

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const cancelSubscriptionHandler = () => {
    setIsCancelModalOpen(true);
  };
  const {hasSeenCurrentPricingPageVersion} = useOnboardingFlags();
  const forceOnboarding = onboardingWizardStore(
    useShallow((state) => state.forceOnboarding)
  );
  const isOnLaunch = !hasSeenCurrentPricingPageVersion || forceOnboarding;

  const isTrialFeatureEnabled = ltxScalerFreeTrial && isOnLaunch;

  useEffect(() => {
    // Use the same pricing flow_id if the user was redirected to web payments
    // We check it here and not in ContentStudio since ReactRouter rendering from inside-out and this happens first
    const storedFlowId = getContentLabWebPaymentFlowId();
    if (storedFlowId) {
      AnalyticsService.createFlow(FLOW_NAMES.CONTENT_STUDIO.PRICING, {
        flow_id: storedFlowId,
      });
    }
    removeContentLabWebPaymentFlowId();

    // Assuming the flow has been initialized with the source before redirecting to the pricing page.
    const flow = AnalyticsService.getOrCreateFlow(
      FLOW_NAMES.CONTENT_STUDIO.PRICING
    );
    if (!flow.source || !skippedFlowStartedEventSources.includes(flow.source)) {
      storeFlowStarted({
        flow_id: flow.flow_id,
        product_type: 'subscription',
        source: isOnLaunch ? 'subscription_on_launch' : flow.source || '',
      });
    }

    presentation_id = uuid();
    storeViewPresented({
      presentation_id,
      view_name: CONTENT_LAB_VIEW_NAMES.PRICING,
      view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_PAGE,
    });

    // runs when component unmounts
    return () => {
      sendStoreViewDismissedEvent(presentation_id);
    };
  }, []);

  const renewAtMs = activeSubscription?.renewAtMs;
  const subscriptionCycleEndDate = renewAtMs ? new Date(renewAtMs) : null;
  const renewAtDaysFromNow = getDaysUntil(subscriptionCycleEndDate);

  const handleBackClick = () => {
    const previousPath = previousLocation?.pathname;
    if (
      previousPath.startsWith(ROUTES.CONTENT_LAB) &&
      previousPath !== `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PRICING}`
    ) {
      navigation.goBack();
    } else {
      navigation.navigate(ROUTES.CONTENT_LAB);
    }
  };

  const sendStoreViewDismissedEvent = (presentationId: string) => {
    storeViewDismissed({
      presentation_id: presentationId,
      view_name: CONTENT_LAB_VIEW_NAMES.PRICING,
      view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_PAGE,
    });
  };

  const sendUiInteractionEvent = useCallback(
    (buttonName: string) => {
      uiInteraction({
        item_name: buttonName,
        interaction_location: CONTENT_LAB_VIEW_NAMES.PRICING,
        flow_name: FLOW_NAMES.CONTENT_STUDIO.PRICING,
        presentation_id,
        interaction_id: uuid(),
      });
    },
    [presentation_id]
  );

  const sendPurchaseProcessStartedEvent = useCallback(
    (product_id: string, process_id?: string) => {
      purchaseProcessStarted({
        presentation_id,
        process_id: process_id || uuid(),
        product_id,
        product_name: product_id,
      });
    },
    [presentation_id]
  );

  const {handleCtaClick} = useHandlePricingCtaClick({
    shouldSkipUpgradeModal: true,
    sendUiInteractionEvent,
    sendPurchaseProcessStartedEvent,
    enforcePaymentManagerRole: true,
  });

  const onContactSalesClick = useContactSales({
    flowName: 'pricing-page-contact-sales',
    interactionLocation: `pricing-page`,
  });
  const renderSubscribeButton = (currentPlan: SubscriptionPlan) => {
    const useTrialQuoteId = false;
    const buttonText =
      currentPlan.id === activePlan?.id ? `Current Plan` : `Get Started`;
    return (
      <PricingCtaButton
        onClick={() => {
          if (currentPlan) {
            handleCtaClick(
              activeSubscription?.id,
              activePlan,
              currentPlan,
              buttonText,
              useTrialQuoteId
            );
          }
        }}
        isHighlighted={false}
        isDisabled={currentPlan?.isDisabled || false}
        isCurrentPlan={currentPlan.id === activePlan?.id}
        text={translate(buttonText)}
      />
    );
  };

  if (!isPlansDataFetched || isFetchingCurrentSubscription) {
    return <LtxScalerLoader />;
  }

  const handleCancelSubscription = async () => {
    const showErrorToast = () => {
      contentLabActions.addError({
        title: 'Error',
        description: 'Failed to cancel subscription, please contact support',
        kind: ErrorKind.FAILURE,
      });
    };
    try {
      if (!activeSubscription?.id) {
        showErrorToast();
        return;
      }

      await cancelSubscription(activeSubscription.id);

      raiseFlashMessage({
        status: 'success',
        mode: 'light',
        message: isTrialPlan(activeSubscription) ? (
          <Stack direction="column" gap={0.5}>
            <Headline size="xs">{translate('Free Trial Canceled')}</Headline>
            <Body size="md">
              {translate(
                'You’ll retain access to all credits until the end of your current billing cycle.'
              )}
            </Body>
          </Stack>
        ) : (
          translate('Subscription cancelled successfully')
        ),
      });
    } catch (error) {
      showErrorToast();
      if (error instanceof Error) {
        Sentry.captureException(error, {
          title: 'Error cancelling subscription',
          activeSubscription,
          activePlan,
        });
      }
    } finally {
      setIsCancelModalOpen(false);
    }
  };

  const Header = styled(Stack)({
    width: '100%',
    gap: '8px',
    marginBottom: '36px',
    justifyContent: 'center',
  });

  const MainContainer = styled(Stack)({
    width: '100%',
    justifyContent: 'center',
    maxHeight: '100%',
  });

  const headerTexts = {
    trial: {
      headline: translate('🎉 Limited-Time Offer: Try Pro Free for 7 Days'),
      body: translate('Only If You Start Now'),
    },
    launch: {
      headline: translate('You’re in! Choose your plan'),
      body: translate('Save up to 50% with annual billing'),
    },
    default: {
      headline: translate('Find the plan that’s right for you'),
      body: translate(
        'Start scaling your content faster and smarter with AI-powered tools. Cancel anytime'
      ),
    },
  };

  const getCurrentHeaderTexts = () => {
    if (isTrialFeatureEnabled) return headerTexts.trial;
    if (isOnLaunch) return headerTexts.launch;
    return headerTexts.default;
  };

  const {headline: headerHeadlineText, body: headerBodyText} =
    getCurrentHeaderTexts();

  return (
    <div className={styles.pricingPage}>
      <div
        className={styles.topBar}
        style={{
          justifyContent: isOnLaunch ? 'flex-end' : 'space-between',
          marginBottom: isOnLaunch ? '0px' : '',
        }}
      >
        {!isOnLaunch && (
          <Button
            appearance="neutral"
            mode="plain"
            size="medium"
            className={styles.backButton}
            icon={
              <Icon
                size="medium"
                appearance="neutral"
                name="Actions-Arrow-Back-Small"
              />
            }
            onClick={handleBackClick}
          >
            {translate('Back')}
          </Button>
        )}
        {isOnLaunch || isTrialFeatureEnabled ? (
          <Button
            appearance="neutral"
            mode="plain"
            size="large"
            onClick={() => {
              navigation.navigate(ROUTES.CONTENT_LAB);
              sendUiInteractionEvent('skip');
            }}
            className={styles.skipButton}
          >
            {isTrialFeatureEnabled
              ? translate('Skip Free Trial')
              : translate('Skip')}
          </Button>
        ) : (
          <Link to={ROUTES.CONTENT_LAB} className={styles.CSLogo}>
            <ContentLabLogo />
          </Link>
        )}
      </div>
      {isTrialPeriod && (
        <div className={styles.trialBannerContainer}>
          <TrialBanner
            daysLeft={renewAtDaysFromNow}
            onStartSubscription={() =>
              openTrialUpgradeModal({
                source: 'trial_banner',
                reason: FreeTrialEndModalReason.UPGRADE,
                onSuccess: handleBackClick,
              })
            }
            isCancelled={!activeSubscription?.isAutoRenewEnabled}
          />
        </div>
      )}
      <Header>
        <Headline size="xl" align="center">
          {headerHeadlineText}
        </Headline>
        <Body
          size="xl"
          align="center"
          color={designSystemToken('semantic.fg.secondary')}
        >
          {headerBodyText}
          {isTrialFeatureEnabled ? '! ' : '. '}
        </Body>
      </Header>
      <MainContainer>
        <div className={styles.pricingBundles}>
          {plansCollections?.map((plansCollection: PlansCollection) => {
            if (plansCollection.plans.length === 1)
              return (
                <PricingBundle
                  key={plansCollection.id}
                  plan={plansCollection.plans[0]}
                  bundle={plansCollection.bundle}
                  name={getBundleLabel(plansCollection.bundle)}
                  description={getBundleDescription(plansCollection.bundle)}
                  priceFootnote={getBundlePriceFootnote(plansCollection.bundle)}
                  isActiveBundle={plansCollection.bundle === activePlan?.bundle}
                  button={renderSubscribeButton(plansCollection.plans[0])}
                />
              );
            if (plansCollection.plans.length > 1)
              return (
                <MultiPlansPricingBundle
                  key={plansCollection.id}
                  plans={plansCollection.plans}
                  bundle={plansCollection.bundle}
                  name={getBundleLabel(plansCollection.bundle)}
                  description={getBundleDescription(plansCollection.bundle)}
                  isHighlighted={
                    plansCollection.bundle === ContentLabPlanBundle.PRO
                  }
                  isActiveBundle={plansCollection.bundle === activePlan?.bundle}
                  isOnLaunch={isOnLaunch}
                  isTrialFeatureEnabled={isTrialFeatureEnabled}
                  activePlan={activePlan}
                  activeSubscriptionId={activeSubscription?.id}
                  sendUiInteractionEvent={sendUiInteractionEvent}
                  sendPurchaseProcessStartedEvent={
                    sendPurchaseProcessStartedEvent
                  }
                  enforcePaymentManagerRole
                />
              );
            if (plansCollection.bundle === ContentLabPlanBundle.ENTERPRISE)
              return (
                <PricingBundle
                  key={plansCollection.id}
                  plan={null}
                  bundle={plansCollection.bundle}
                  name={getBundleLabel(plansCollection.bundle)}
                  description={getBundleDescription(plansCollection.bundle)}
                  priceFootnote={getBundlePriceFootnote(plansCollection.bundle)}
                  isActiveBundle={false}
                  button={
                    <ContactSalesButton
                      onContactSalesClick={onContactSalesClick}
                    />
                  }
                />
              );
            return null;
          })}
        </div>
        <BottomSubscriptionInfo
          activeSubscription={activeSubscription}
          cancelSubscriptionHandler={cancelSubscriptionHandler}
        />
      </MainContainer>

      <CancelSubscriptionModal
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelSubscription}
        planName={getBundleLabel(activePlan?.bundle ?? '')}
        cycleEndDate={subscriptionCycleEndDate}
        isLoading={isCancelSubscriptionInProgress}
        primaryButtonClassName={styles.cancelSubscriptionButton}
        contentClassName={styles.modalContent}
        source={CONTENT_LAB_VIEW_NAMES.PRICING}
        isTrial={isTrialPlan(activeSubscription)}
      />
    </div>
  );
}

export default ContentLabPricingPage;
